import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import './mobile-navbar.css';

import { ReactComponent as BookingIcon } from '../../assets/images/navbar-booking.svg';
import { ReactComponent as AboutIcon } from '../../assets/images/navbar-about.svg';
import { ReactComponent as LoyaltyIcon } from '../../assets/images/navbar-loyalty.svg';
import { ReactComponent as MenuIcon } from '../../assets/images/navbar-menu.svg';
import { ReactComponent as ProfileIcon } from '../../assets/images/navbar-profile.svg';

import {
  setState as setPropsState,
  toggleMobileCart,
} from 'containers/Home/actions';
import { LOGIN_FROM_TYPE, ORDER_STAGE_TYPES } from 'helpers/enums';
import { useAuth } from 'contexts/AuthProvider';
import { PAGE_TRANSITION_DURATION } from 'containers/Home/constants';

export const onClickDefaultState = {
  loyaltyOpen: false,
  aboutOpen: false,
  bookingOpen: false,
  profileOpen: false,
};

const FloatingNavbarMobile = ({ showLoyaltyButton }) => {
  const dispatch = useDispatch();
  const {
    setState: setAuthState,
    authOrderStage,
    loginFrom,
    loading: authLoading,
  } = useAuth();
  const {
    orderSetup = {},
    loyaltyOpen,
    aboutOpen,
    bookingOpen,
    profileOpen,
    currentOrder,
    userDetails,
  } = useSelector((state) => state.homeReducer);

  const {
    isTableBookingOn = false,
    useCustomTableBooking = false,
    customBookingURL = '',
  } = orderSetup;

  const getActiveTab = () => {
    if (aboutOpen) return 'About';
    if (
      loyaltyOpen ||
      (authOrderStage === ORDER_STAGE_TYPES.OAUTH_FORM &&
        loginFrom === LOGIN_FROM_TYPE.LOYALTY)
    )
      return 'Loyalty';
    if (bookingOpen) return 'Booking';
    if (profileOpen) return 'Profile';
    if (
      authOrderStage === ORDER_STAGE_TYPES.OAUTH_FORM &&
      loginFrom === LOGIN_FROM_TYPE.PROFILE
    )
      return 'Log In';
    return 'Menu';
  };

  const active = getActiveTab();

  const handleClick = (name) => {
    if (name === 'About') {
      dispatch(toggleMobileCart(true));
      setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
      dispatch(setPropsState({ ...onClickDefaultState, aboutOpen: true }));
    } else if (name === 'Loyalty') {
      dispatch(toggleMobileCart(true));
      setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
      dispatch(setPropsState({ ...onClickDefaultState, loyaltyOpen: true }));
    } else if (name === 'Booking') {
      if (useCustomTableBooking && customBookingURL) {
        window.open(customBookingURL, '_blank');
      } else {
        dispatch(toggleMobileCart(true));
        setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
        dispatch(setPropsState({ ...onClickDefaultState, bookingOpen: true }));
      }
    } else if (name === 'Profile' || name === 'Log In') {
      if (userDetails && userDetails.firstName) {
        dispatch(toggleMobileCart(true));
        setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
        dispatch(setPropsState({ ...onClickDefaultState, profileOpen: true }));
      } else {
        dispatch(toggleMobileCart(true));
        setAuthState({
          authOrderStage: ORDER_STAGE_TYPES.OAUTH_FORM,
          loginFrom: LOGIN_FROM_TYPE.PROFILE,
        });
        dispatch(setPropsState({ ...onClickDefaultState }));
      }
    } else if (name === 'Menu') {
      dispatch(toggleMobileCart(false));
      // setTimeout is to avoid the mobile cart appearing behind sidebar when its fading out
      setTimeout(() => {
        setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
        dispatch(
          setPropsState({
            ...onClickDefaultState,
            currentOrder: {
              ...currentOrder,
              stage: ORDER_STAGE_TYPES.NONE,
            },
          })
        );
      }, PAGE_TRANSITION_DURATION);
    }
  };

  // Build nav items dynamically
  const navItems = [
    { name: 'Menu', icon: MenuIcon },
    ...(isTableBookingOn && 'useCustomTableBooking' in orderSetup
      ? [{ name: 'Booking', icon: BookingIcon }]
      : []),
    ...(showLoyaltyButton ? [{ name: 'Loyalty', icon: LoyaltyIcon }] : []),
    { name: 'About', icon: AboutIcon },
    // Show skeleton for Profile/Log In while loading, otherwise show based on user state
    ...(authLoading
      ? [{ name: 'Loading', icon: ProfileIcon, isSkeleton: true }]
      : userDetails && userDetails.firstName
      ? [{ name: 'Profile', icon: ProfileIcon }]
      : [{ name: 'Log In', icon: ProfileIcon }]),
  ];

  const bubbleClass = classNames(
    'mobile-navbar fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-50'
  );

  return (
    <div className={bubbleClass}>
      <div className='flex justify-between items-center h-[63px]'>
        {navItems.map(({ name, icon: Icon, isSkeleton }) => {
          const isActive = active === name;

          // Show skeleton loader for loading state - must match exact button size
          if (isSkeleton) {
            // Render actual button structure but with skeleton styling to prevent layout shift
            return (
              <button
                key={name}
                disabled
                className='flex flex-col items-center text-[10px] focus:outline-none opacity-50'
                style={{ cursor: 'default' }}
              >
                <div className='relative'>
                  {/* Use actual icon but make it look like skeleton */}
                  <Icon
                    className='menu-icon'
                    style={{
                      fill: 'rgb(229, 231, 235)',
                      filter: 'opacity(0.6)',
                    }}
                  />
                </div>
                {/* Use actual text "Profile" to match width, but make it look like skeleton */}
                <span
                  className='nav-text'
                  style={{
                    fontWeight: 500,
                    color: 'rgb(229, 231, 235)',
                  }}
                >
                  Profile
                </span>
              </button>
            );
          }

          return (
            <button
              key={name}
              onClick={() => handleClick(name)}
              className='flex flex-col items-center text-[10px] focus:outline-none'
            >
              <div className='relative'>
                <Icon
                  className={classNames('menu-icon', {
                    'active-icon': isActive,
                    'ml-[2px]': name === 'About',
                  })}
                />
                {/* Small dot if Menu and cart has items */}
                {name === 'Menu' &&
                  currentOrder.menuItems &&
                  currentOrder.menuItems.length > 0 && (
                    <span
                      className='absolute block h-1.5 w-1.5 rounded-full ring-2 ring-white'
                      style={{
                        top: '-1.5px',
                        right: '-2px',
                        backgroundColor: 'var(--main-theme-color)',
                      }}
                    />
                  )}
              </div>
              <span
                className={classNames('nav-text', {
                  'text-primary': isActive,
                  'active-text': isActive,
                })}
                data-text={name}
              >
                {name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FloatingNavbarMobile;
